import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem } from '@mui/material';

import { useGetEmployees } from '../../hooks/employees/useGetEmployees';
import { useDeleteEmployee } from '../../hooks/employees/useDeleteEmployee';
import { useDeleteAllEmployees } from '../../hooks/employees/useDeleteAllEmployees';
import { useForceDeleteEmployee } from '../../hooks/employees/useForceDeleteEmployee';
import { useForceDeleteAllEmployees } from '../../hooks/employees/useForceDeleteAllEmployees';
import { useRestoreEmployee } from '../../hooks/employees/useRestoreEmployee';
import { useRestoreAllEmployees } from '../../hooks/employees/useRestoreAllEmployees';
import { useGetDepartments } from '../../hooks/departments/useGetDepartments';
import { useGetJobTitles } from '../../hooks/jobTitles/useGetJobTitles';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import EmployeesTableColumns from './Table/EmployeesTableColumns';
import EmployeesTableRow from './Table/EmployeesTableRow';
import ModelShowEmployee from './ModelShowEmployee';

const EmployeesTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const [filterDepartmentId, setFilterDepartmentId] = useState('');
    const [filterJobTitleId, setFilterJobTitleId] = useState('');
    const [openView, setOpenView] = useState(false);
    const [viewId, setViewId] = useState(null);

    const perms = useMemo(() => ({
        list: has('employees_list'),
        create: has('employees_create'),
        edit: has('employees_edit'),
        show: has('employees_show'),
        delete: has('employees_delete'),
        archive_list: has('employees_archive_list'),
        restore: has('employees_restore'),
        force_delete: has('employees_force_delete'),
    }), [has]);

    const { deleteEmployee, isDelete } = useDeleteEmployee();
    const { deleteAllEmployees, isDelete: isDeletingAll } = useDeleteAllEmployees();
    const { forceDeleteEmployee } = useForceDeleteEmployee();
    const { forceDeleteAllEmployees } = useForceDeleteAllEmployees();
    const { restoreEmployee } = useRestoreEmployee();
    const { restoreAllEmployees } = useRestoreAllEmployees();
    const { departments } = useGetDepartments(1, 999);
    const { jobTitles } = useGetJobTitles(1, 999, { department_id: filterDepartmentId || undefined });

    const {
        page, perPage, search, sortBy, sortDir, selectedIds, menuAnchorEl, selectedItem,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: '/employees',
        deleteSingle: deleteEmployee, deleteAll: deleteAllEmployees,
        forceDeleteSingle: forceDeleteEmployee, forceDeleteAll: forceDeleteAllEmployees,
        restoreSingle: restoreEmployee, restoreAll: restoreAllEmployees,
        isArchive,
    });

    const handleView = () => {
        if (selectedItem) { setViewId(selectedItem.id); setOpenView(true); }
        handleMenuClose();
    };

    const filters = useMemo(() => ({
        search: search || undefined,
        sort_by: sortBy || undefined, sort_dir: sortDir || undefined,
        department_id: filterDepartmentId || undefined,
        job_title_id: filterJobTitleId || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, filterDepartmentId, filterJobTitleId, isArchive]);

    const { isLoading, error, employees, meta } = useGetEmployees(page, perPage, filters);

    const rtlSelectSx = isArabic ? {
        '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
        '& .MuiInputLabel-shrink': { right: 32 },
        '& .MuiSelect-icon': { left: 7, right: 'unset' },
        '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
        '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
    } : {};

    const renderRow = (params) => (
        <EmployeesTableRow
            key={params.key || params.item.id} item={params.item} index={params.index}
            isArabic={params.isArabic} isDark={params.isDark}
            page={params.page} perPage={params.perPage} order={params.order}
            totalItems={employees.length} selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick} onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <TextField select size="small" value={filterDepartmentId}
                    onChange={(e) => { setFilterDepartmentId(e.target.value); setFilterJobTitleId(''); }}
                    label={isArabic ? 'القسم' : 'Department'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 200, ...rtlSelectSx }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}
                >
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {(departments || []).map((d) => (<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>))}
                </TextField>

                <TextField select size="small" value={filterJobTitleId}
                    onChange={(e) => setFilterJobTitleId(e.target.value)}
                    label={isArabic ? 'المسمى الوظيفي' : 'Job Title'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 200, ...rtlSelectSx }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}
                >
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {(jobTitles || []).map((jt) => (<MenuItem key={jt.id} value={jt.id}>{jt.name}</MenuItem>))}
                </TextField>

                <Box sx={{ marginInlineStart: 'auto' }}>
                    {!isArchive && perms.archive_list && <ArchiveButton to="/employees/archive" />}
                    {isArchive && perms.list && <ArchiveButton to="/employees" archiveLabelAr="العودة للقائمة" archiveLabelEn="Back to List" />}
                </Box>
            </Box>

            <TableLayout
                title={isArabic ? (isArchive ? 'أرشيف الموظفين' : 'الموظفون') : (isArchive ? 'Employees Archive' : 'Employees')}
                subtitle={isArabic ? (isArchive ? 'إدارة الموظفين المحذوفين' : 'إدارة الموظفين في النظام') : (isArchive ? 'Manage deleted employees' : 'Manage employees')}
                search={search} onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة موظف' : 'Add Employee'}
                onAddClick={() => navigate('/employees/create')}
                addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading} error={error} items={employees} meta={meta}
                page={page} perPage={perPage} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}
                sortBy={sortBy} sortDir={sortDir} onRequestSort={handleRequestSort}
                selectedIds={selectedIds} onSelectAllClick={(e) => handleSelectAllClick(e, employees)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={EmployeesTableColumns} renderRow={renderRow}
                menuAnchorEl={menuAnchorEl} openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen} onMenuClose={handleMenuClose}
                onMenuView={perms.show ? handleView : undefined}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive ? (perms.force_delete ? handleMenuDelete : undefined) : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit}
                menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore}
                isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا يوجد موظفون في الأرشيف' : 'لا يوجد موظفون'}
                emptyLabelEn={isArchive ? 'No archived employees' : 'No employees'}
                loadErrorAr="حدث خطأ أثناء تحميل الموظفين" loadErrorEn="Failed to load employees"
            />

            {openView && <ModelShowEmployee employeeId={viewId} open={openView} onClose={() => setOpenView(false)} />}
        </>
    );
};

export default EmployeesTable;
